/**
 * Shared components and utilities used by PremiumLandingScreen and BuyPremiumScreen.
 */
import React from 'react'
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Image,
  ResponsiveContext,
  Text
} from 'grommet'
import { Checkmark } from 'grommet-icons'
import { I18n, Constants } from 'galarm-config'

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

export const formatPrice = price => {
  if (!price) return ''
  const unitAmount =
    typeof price.unit_amount === 'number'
      ? price.unit_amount
      : typeof price.unit_amount_decimal === 'string'
      ? Number(price.unit_amount_decimal)
      : null
  const currency = (price.currency || 'usd').toUpperCase()
  if (unitAmount === null || !Number.isFinite(unitAmount)) return ''
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(unitAmount / 100)
  } catch {
    return `$${(unitAmount / 100).toFixed(2)}`
  }
}

// ---------------------------------------------------------------------------
// PlanCard
// ---------------------------------------------------------------------------

export const PlanCard = ({
  label,
  price,
  selected,
  onSelect,
  readOnly = false
}) => (
  <Card
    flex
    border={{
      color: selected ? 'brand' : 'light-4',
      size: selected ? 'medium' : 'small'
    }}
    background={selected ? 'light-1' : 'white'}
    onClick={readOnly ? undefined : onSelect}
    style={{ cursor: readOnly ? 'default' : 'pointer' }}>
    <CardHeader pad="medium" background={selected ? 'brand' : 'light-2'}>
      <Text
        size="large"
        weight="bold"
        color={selected ? 'white' : 'dark-1'}
        style={{ whiteSpace: 'nowrap' }}>
        {label}
      </Text>
      {selected && <Checkmark color="white" size="medium" />}
    </CardHeader>
    <CardBody pad="medium">
      <Text size="xlarge" weight="bold" color="brand">
        {price}
      </Text>
    </CardBody>
  </Card>
)

// ---------------------------------------------------------------------------
// PlanCards — renders both monthly and yearly plan cards side by side.
// Receives the price objects and the current selection from the parent.
// ---------------------------------------------------------------------------

export const PlanCards = ({
  monthlyPrice,
  yearlyPrice,
  lifetimePrice,
  selectedInterval,
  onIntervalChange,
  readOnly = false
}) => {
  const size = React.useContext(ResponsiveContext)
  const isSmallScreen = size === 'small'

  if (!monthlyPrice && !yearlyPrice && !lifetimePrice) return null
  return (
    <Box direction={isSmallScreen ? 'column' : 'row'} gap="medium">
      {monthlyPrice && (
        <PlanCard
          label={I18n.t('buyPremiumMonthly')}
          price={I18n.t('monthlyPrice', { price: formatPrice(monthlyPrice) })}
          selected={selectedInterval === 'month'}
          onSelect={() => onIntervalChange('month')}
          readOnly={readOnly}
        />
      )}
      {yearlyPrice && (
        <PlanCard
          label={I18n.t('buyPremiumYearly')}
          price={I18n.t('yearlyPrice', { price: formatPrice(yearlyPrice) })}
          selected={selectedInterval === 'year'}
          onSelect={() => onIntervalChange('year')}
          readOnly={readOnly}
        />
      )}
      {lifetimePrice && (
        <PlanCard
          label={I18n.t('buyPremiumLifetime')}
          price={I18n.t('lifetimePrice', { price: formatPrice(lifetimePrice) })}
          selected={selectedInterval === 'one_time'}
          onSelect={() => onIntervalChange('one_time')}
          readOnly={readOnly}
        />
      )}
    </Box>
  )
}

// ---------------------------------------------------------------------------
// FreeTrialBanner
// ---------------------------------------------------------------------------

export const FreeTrialBanner = () => (
  <Box
    background="#f0f7ff"
    round="small"
    pad={{ horizontal: 'medium', vertical: 'small' }}
    direction="row"
    gap="xsmall"
    align="center">
    <Text size="small" color="dark-3">
      {I18n.t('tryPremiumFreeCancelAnytime')}
    </Text>
  </Box>
)

// ---------------------------------------------------------------------------
// FeatureRow
// ---------------------------------------------------------------------------

export const FeatureRow = ({ feature }) => {
  const imageSrc = feature.imageSource?.default || feature.imageSource
  return (
    <Box
      direction="row"
      align="center"
      gap="small"
      pad={{ vertical: '8px' }}
      border={{ side: 'bottom', color: '#f0f0f4' }}>
      <Box
        width="36px"
        height="36px"
        round="small"
        background="light-2"
        align="center"
        justify="center"
        flex={false}>
        <Image fit="contain" src={imageSrc} height="22px" width="22px" />
      </Box>
      <Box flex>
        <Text size="small" weight="bold" color="dark-2">
          {feature.title}
        </Text>
        {feature.description && (
          <Text size="xsmall" color="dark-4">
            {feature.description}
          </Text>
        )}
      </Box>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// FeatureList — renders all premium features in a white card.
// ---------------------------------------------------------------------------

export const FeatureList = () => (
  <Box
    background="white"
    round="small"
    pad={{ horizontal: 'medium', vertical: 'small' }}
    style={{ boxShadow: '0 1px 8px rgba(0,0,0,0.07)' }}>
    <Text
      size="small"
      weight="bold"
      color="dark-4"
      style={{
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        marginBottom: '8px'
      }}>
      {I18n.t('accessToPremiumFeatures')}
    </Text>
    {Constants.PREMIUM_FEATURES.map(feature => (
      <FeatureRow key={feature.id} feature={feature} />
    ))}
  </Box>
)
